import { formatHours, type ApprovalInboxEntry } from '@time-tracking/shared';

import { hoursBucket, hoursBucketClass, type HoursBucket } from '../../utils/hours.js';
import { StatusPill } from '../StatusPill.js';

// Spoken form of each bucket, so the hours cell never relies on colour alone.
const HOURS_BUCKET_LABEL: Record<HoursBucket, string> = {
  zero: 'none logged',
  under: 'under target',
  target: 'on target',
  over: 'over target',
};

interface InboxTableProps {
  entries: ApprovalInboxEntry[];
  pendingApproveIds: ReadonlySet<string>;
  pendingUnapproveIds: Set<string>;
  onRowClick: (entry: ApprovalInboxEntry) => void;
  onApprove: (entry: ApprovalInboxEntry) => void;
  onReturn: (entry: ApprovalInboxEntry) => void;
  onUnapprove: (entry: ApprovalInboxEntry) => void;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  reviewOnly?: boolean;
  assignMode?: boolean;
  onOpenPicker?: (userIds: string[], anchorRect: DOMRect) => void;
  selectedUserIds?: Set<string>;
  onUserSelectionChange?: (ids: Set<string>) => void;
  currentUserId?: string;
  /**
   * A closed reporting period owns this week. Approve, Return and Unapprove would
   * all be refused, so the row says so in the action cell instead of offering them.
   * The row itself stays — lines never disappear — and still opens the drawer.
   */
  periodClosed?: boolean;
}

export function InboxTable({
  entries,
  pendingApproveIds,
  pendingUnapproveIds,
  onRowClick,
  onApprove,
  onReturn,
  onUnapprove,
  selectable,
  selectedIds,
  onSelectionChange,
  reviewOnly,
  assignMode,
  onOpenPicker,
  selectedUserIds,
  onUserSelectionChange,
  currentUserId,
  periodClosed = false,
}: InboxTableProps) {
  const selectableIds = entries
    .filter((e) => e.status === 'submitted' && e.timesheetId !== null)
    .map((e) => e.timesheetId as string);
  const assignableUserIds = assignMode ? entries.map((e) => e.userId) : [];

  if (entries.length === 0) {
    return (
      <div className="inbox-empty" role="status">
        No timesheets to display for this period.
      </div>
    );
  }

  return (
    <div className="inbox-table-wrap">
      <table className="inbox-table">
        <caption className="sr-only">Team timesheets</caption>
        <thead>
          <tr>
            <th scope="col">
              {selectable && !assignMode && selectableIds.length > 0 && (
                <input
                  type="checkbox"
                  aria-label="Select all submitted timesheets"
                  checked={
                    selectableIds.length > 0 && selectableIds.every((id) => selectedIds?.has(id))
                  }
                  ref={(el) => {
                    if (el) {
                      el.indeterminate =
                        selectableIds.some((id) => selectedIds?.has(id)) &&
                        !selectableIds.every((id) => selectedIds?.has(id));
                    }
                  }}
                  onChange={(e) => {
                    if (!onSelectionChange) return;
                    if (e.target.checked) {
                      onSelectionChange(new Set([...(selectedIds ?? []), ...selectableIds]));
                    } else {
                      const next = new Set(selectedIds ?? []);
                      selectableIds.forEach((id) => next.delete(id));
                      onSelectionChange(next);
                    }
                  }}
                />
              )}
              {assignMode && (
                <input
                  type="checkbox"
                  aria-label="Select all unassigned people"
                  checked={
                    assignableUserIds.length > 0 &&
                    assignableUserIds.every((id) => selectedUserIds?.has(id))
                  }
                  ref={(el) => {
                    if (el) {
                      el.indeterminate =
                        assignableUserIds.some((id) => selectedUserIds?.has(id)) &&
                        !assignableUserIds.every((id) => selectedUserIds?.has(id));
                    }
                  }}
                  onChange={(e) => {
                    if (!onUserSelectionChange) return;
                    onUserSelectionChange(
                      e.target.checked ? new Set(assignableUserIds) : new Set(),
                    );
                  }}
                />
              )}
            </th>
            <th scope="col">Person</th>
            <th scope="col">Hours</th>
            <th scope="col">Status</th>
            <th scope="col">Anomalies</th>
            <th scope="col">Submitted</th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => {
            const showsRealHours = entry.status === 'submitted' || entry.status === 'approved';
            const displayMinutes = showsRealHours ? entry.totalMinutes : 0;
            const hours = formatHours(displayMinutes);
            const target = formatHours(entry.targetMinutes);
            const bucket = hoursBucket(displayMinutes, entry.targetMinutes);
            const submittedDate = entry.submittedAt
              ? new Intl.DateTimeFormat('en-US', {
                  month: 'short',
                  day: 'numeric',
                }).format(new Date(entry.submittedAt))
              : '—';

            return (
              <tr
                key={entry.userId}
                className={`inbox-row${entry.teamId === null ? ' inbox-row--unassigned' : ''}`}
                onClick={() => onRowClick(entry)}
              >
                <td onClick={(e) => e.stopPropagation()}>
                  {assignMode ? (
                    <input
                      type="checkbox"
                      aria-label={`Select ${entry.name}`}
                      checked={selectedUserIds?.has(entry.userId) ?? false}
                      onChange={(e) => {
                        if (!onUserSelectionChange) return;
                        const next = new Set(selectedUserIds ?? []);
                        if (e.target.checked) {
                          next.add(entry.userId);
                        } else {
                          next.delete(entry.userId);
                        }
                        onUserSelectionChange(next);
                      }}
                    />
                  ) : selectable && entry.status === 'submitted' && entry.timesheetId ? (
                    <input
                      type="checkbox"
                      aria-label={`Select ${entry.name}'s timesheet`}
                      checked={selectedIds?.has(entry.timesheetId) ?? false}
                      onChange={(e) => {
                        if (!onSelectionChange || !entry.timesheetId) return;
                        const next = new Set(selectedIds ?? []);
                        if (e.target.checked) {
                          next.add(entry.timesheetId);
                        } else {
                          next.delete(entry.timesheetId);
                        }
                        onSelectionChange(next);
                      }}
                    />
                  ) : null}
                </td>
                <td className="inbox-cell inbox-cell--person">
                  <button
                    type="button"
                    className="row-name-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRowClick(entry);
                    }}
                    // The label overrides descendant text, so the EID has to be
                    // named explicitly — otherwise the disambiguator sighted users
                    // get between similar names never reaches a screen reader.
                    aria-label={
                      entry.utEid
                        ? `View ${entry.name}'s timesheet details, ${entry.utEid}`
                        : `View ${entry.name}'s timesheet details`
                    }
                  >
                    <span className="person-avatar" aria-hidden="true">
                      {entry.initials}
                    </span>
                    <span className="person-identity">
                      <span className="person-name">
                        {entry.name}
                        {currentUserId !== undefined && entry.userId === currentUserId && (
                          <span className="row-self-tag">your timesheet</span>
                        )}
                      </span>
                      {entry.utEid && <span className="person-eid">{entry.utEid}</span>}
                    </span>
                  </button>
                </td>
                <td className="inbox-cell inbox-cell--hours">
                  <span
                    className={hoursBucketClass(bucket)}
                    // The bucket phrase is an interpretation, so it is only spoken
                    // when the figure is real. Draft/returned/missing rows force
                    // displayMinutes to 0, and announcing "none logged" over hidden
                    // hours would state something untrue.
                    aria-label={
                      showsRealHours
                        ? `${hours} of ${target} hours — ${HOURS_BUCKET_LABEL[bucket]}`
                        : `${hours} of ${target} hours`
                    }
                  >
                    {hours} / {target}
                  </span>
                </td>
                <td className="inbox-cell">
                  <StatusPill status={entry.status} />
                </td>
                <td className="inbox-cell inbox-cell--anomalies">
                  {entry.anomalies.length > 0 ? (
                    <span
                      className="anomaly-badge"
                      aria-label={`${entry.anomalies.length} anomaly warning${entry.anomalies.length > 1 ? 's' : ''}`}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M8 1L1 14h14L8 1zm0 2.8L13.1 13H2.9L8 3.8zm-.75 3.7v2.5h1.5V7.5h-1.5zm0 3.5v1.5h1.5V11h-1.5z" />
                      </svg>
                      {entry.anomalies.length}
                    </span>
                  ) : (
                    <span className="sr-only">No anomalies</span>
                  )}
                </td>
                <td className="inbox-cell inbox-cell--submitted">{submittedDate}</td>
                <td className="inbox-cell inbox-cell--actions" onClick={(e) => e.stopPropagation()}>
                  {assignMode ? (
                    <>
                      {entry.status === 'submitted' && (
                        <button
                          type="button"
                          className="btn btn--sm btn--review"
                          onClick={() => onRowClick(entry)}
                          aria-label={`Review ${entry.name}'s timesheet`}
                        >
                          Review ›
                        </button>
                      )}
                      <button
                        type="button"
                        className="assign-team-btn"
                        aria-label={`Assign ${entry.name} to a team`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenPicker?.([entry.userId], e.currentTarget.getBoundingClientRect());
                        }}
                      >
                        Assign…
                      </button>
                    </>
                  ) : reviewOnly ? (
                    entry.status === 'submitted' && (
                      <button
                        type="button"
                        className="btn btn--sm btn--review"
                        onClick={() => onRowClick(entry)}
                        aria-label={`Review ${entry.name}'s timesheet`}
                      >
                        Review ›
                      </button>
                    )
                  ) : periodClosed &&
                    (entry.status === 'submitted' || entry.status === 'approved') ? (
                    <span className="inbox-row__waiting">Period closed</span>
                  ) : (
                    <>
                      {entry.status === 'submitted' && entry.timesheetId && (
                        <>
                          <button
                            type="button"
                            className="btn btn--sm primary"
                            onClick={() => onApprove(entry)}
                            disabled={pendingApproveIds.has(entry.timesheetId ?? '')}
                            aria-busy={pendingApproveIds.has(entry.timesheetId ?? '')}
                            aria-label={`Approve ${entry.name}'s timesheet`}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="btn btn--sm btn--secondary"
                            onClick={() => onReturn(entry)}
                            aria-label={`Return ${entry.name}'s timesheet for correction`}
                          >
                            Return
                          </button>
                        </>
                      )}
                      {entry.status === 'approved' && entry.timesheetId && (
                        <button
                          type="button"
                          className="btn btn--sm btn--secondary"
                          onClick={() => onUnapprove(entry)}
                          disabled={pendingUnapproveIds.has(entry.timesheetId ?? '')}
                          aria-busy={pendingUnapproveIds.has(entry.timesheetId ?? '')}
                          aria-label={`Unapprove ${entry.name}'s timesheet`}
                        >
                          Unapprove
                        </button>
                      )}
                      {/* These cells were empty. The handoff frames this as removing
                          a Remind button, but none ever shipped here — this adds the
                          label the prototype shows, so the action column says why it
                          is blank rather than just being blank. Deliberately echoes
                          StatusPill, matching the prototype. Assign mode above is
                          untouched and keeps its button on every row. */}
                      {entry.status === 'missing' && (
                        <span className="inbox-row__waiting">Not submitted</span>
                      )}
                      {entry.status === 'draft' && (
                        <span className="inbox-row__waiting">Saved, not submitted</span>
                      )}
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
